import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, AlertCircle, FileDown, Calendar, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const KPICard = ({ icon: Icon, label, value, color = 'text-slate-900', bg = 'bg-slate-100', testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={testId}>
    <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-slate-500 uppercase">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  </div>
);

const Dashboard = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API_URL}/billing/dashboard/summary?year=${year}`);
        setData(r.data);
      } catch { toast.error('Error al cargar KPIs'); }
      finally { setLoading(false); }
    })();
  }, [year]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/billing/dashboard/export-invoices?year=${year}`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `facturas_${year}.csv`;
      a.click();
      toast.success('CSV descargado');
    } catch { toast.error('Error al exportar'); }
  };

  if (loading || !data) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;

  const maxMes = Math.max(...data.facturado_por_mes, 1);

  return (
    <Layout>
      <div className="space-y-6" data-testid="billing-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Dashboard financiero</h1>
            <p className="text-slate-500 mt-1">Resumen anual de facturación, cobros y gastos</p>
          </div>
          <div className="flex gap-2 items-center">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
              {[0, 1, 2, 3, 4].map(off => {
                const y = new Date().getFullYear() - off;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <Button onClick={exportCsv} variant="outline" data-testid="export-csv-button">
              <FileDown className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
        </div>

        {/* KPIs principales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard icon={TrendingUp} label="Facturado" value={money(data.facturado_total)} color="text-blue-700" bg="bg-blue-100" testId="kpi-facturado" />
          <KPICard icon={Wallet} label="Cobrado" value={money(data.total_cobrado)} color="text-green-700" bg="bg-green-100" testId="kpi-cobrado" />
          <KPICard icon={AlertCircle} label="Pendiente cobro" value={money(data.total_pendiente_cobro)} color="text-amber-700" bg="bg-amber-100" testId="kpi-pendiente" />
          <KPICard icon={AlertCircle} label="Vencido" value={money(data.total_vencido)} color="text-red-700" bg="bg-red-100" testId="kpi-vencido" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard icon={TrendingDown} label="Gastos" value={money(data.total_gastos)} color="text-slate-700" bg="bg-slate-100" />
          <KPICard icon={TrendingUp} label="IVA repercutido" value={money(data.iva_repercutido)} color="text-emerald-700" bg="bg-emerald-100" />
          <KPICard icon={TrendingDown} label="IVA soportado" value={money(data.iva_soportado)} color="text-violet-700" bg="bg-violet-100" />
          <KPICard icon={Wallet} label={`IVA neto (${data.iva_neto >= 0 ? 'a pagar' : 'a devolver'})`} value={money(Math.abs(data.iva_neto))} color={data.iva_neto >= 0 ? "text-red-700" : "text-green-700"} bg="bg-red-50" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Gráfica de barras (simple, sin librería) */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="font-manrope font-semibold text-slate-900 mb-4">Facturación mensual {year}</h3>
            <div className="flex items-end gap-2 h-48">
              {data.facturado_por_mes.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] text-slate-500">{v > 0 ? Math.round(v).toLocaleString('es-ES') : ''}</div>
                  <div className="w-full bg-[#004E89] rounded-t transition-all"
                       style={{ height: `${Math.max(2, (v / maxMes) * 100)}%`, opacity: v > 0 ? 1 : 0.15 }}
                       title={`${MONTHS[i]}: ${money(v)}`}></div>
                  <div className="text-xs text-slate-500">{MONTHS[i]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top clientes */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="font-manrope font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> Top clientes
            </h3>
            {data.top_clientes.length === 0 ? (
              <p className="text-sm text-slate-500">Sin facturación este año.</p>
            ) : (
              <ol className="space-y-2 text-sm">
                {data.top_clientes.map((c, i) => (
                  <li key={i} className="flex justify-between items-center gap-2">
                    <span className="truncate">{i + 1}. {c.nombre}</span>
                    <div className="text-right flex-shrink-0">
                      <div className="font-medium">{money(c.total)}</div>
                      <div className="text-xs text-slate-500">{c.count} factura{c.count !== 1 ? 's' : ''}</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Otros datos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500">Nº facturas emitidas</div>
            <div className="text-lg font-bold">{data.num_facturas}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500">Facturas vencidas</div>
            <div className="text-lg font-bold text-red-600">{data.num_facturas_vencidas}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500">Presupuestos ({data.presupuestos_aceptados} aceptados)</div>
            <div className="text-lg font-bold">{data.presupuestos_year}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs text-slate-500">Gastos pendientes</div>
            <div className="text-lg font-bold text-amber-700">{money(data.gastos_pendientes)}</div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
